import type { FilterConditions } from '@/entities/discovery';
import type { SOURCE_PLATFORM_TYPE } from '@/entities/public-filter/constants';
import {
  AGE_MAP,
  GENDER_MAP,
  RACE_MAP,
  countriesList,
  getPlatformName,
  languageList,
} from '@/entities/public-filter/constants';

import { DATE_FORMATS } from '@/shared/utils/date/date-formats';
import dayjs from 'dayjs';

type ParsedCreatorFilter = {
  filterName: string;
  value: string[];
};

const formatFollowersCount = (count: number): string => {
  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(count % 1_000_000 === 0 ? 0 : 1)}M`;
  }
  if (count >= 1_000) {
    return `${(count / 1_000).toFixed(count % 1_000 === 0 ? 0 : 1)}K`;
  }
  return count.toString();
};

const locationMap = new Map(countriesList.map(({ value, label }) => [value, label]));
const languageMap = new Map(languageList.map(({ value, label }) => [value, label]));

export const parseFilterConditions = (filters: FilterConditions): ParsedCreatorFilter[] => {
  const result: ParsedCreatorFilter[] = [];

  if (filters.start_date || filters.end_date) {
    result.push({
      filterName: 'Date range',
      value: [
        `${filters.start_date ? dayjs(filters.start_date).format(DATE_FORMATS.base) : ''} - ${filters.end_date ? dayjs(filters.end_date).format(DATE_FORMATS.base) : ''}`,
      ],
    });
  }

  if (filters.platform?.length) {
    result.push({
      filterName: 'Platform',
      value: filters.platform.map((p) => getPlatformName(p as SOURCE_PLATFORM_TYPE)),
    });
  }

  if (filters.demographics?.location?.length) {
    result.push({
      filterName: 'Location',
      value: filters.demographics.location.map((code) => locationMap.get(code) ?? code),
    });
  }

  if (filters.demographics?.language?.length) {
    result.push({
      filterName: 'Language',
      value: filters.demographics?.language.map((code) => languageMap.get(code) ?? code),
    });
  }

  if (filters.demographics?.age?.length) {
    result.push({
      filterName: 'Age',
      value: filters.demographics?.age.map((age) => AGE_MAP.get(age)),
    });
  }

  if (filters.demographics?.gender?.length) {
    result.push({
      filterName: 'Gender',
      value: filters.demographics?.gender.map((gender) => GENDER_MAP.get(gender)),
    });
  }

  if (filters.demographics?.race?.length) {
    result.push({
      filterName: 'Race',
      value: filters.demographics?.race.map((race) => RACE_MAP.get(race)),
    });
  }

  if (filters.engagement) {
    const { followers_min: min, followers_max: max } = filters.engagement;
    if (min != null || max != null) {
      let rangeStr: string;
      if (min != null && max != null) {
        rangeStr = `${formatFollowersCount(min)} - ${formatFollowersCount(max)}`;
      } else if (min != null) {
        rangeStr = `${formatFollowersCount(min)}+`;
      } else {
        rangeStr = `Up to ${formatFollowersCount(max!)}`;
      }
      result.push({
        filterName: 'Followers range',
        value: [rangeStr],
      });
    }
  }

  if (filters.engagement) {
    const { er_min: min, er_max: max } = filters.engagement;
    if (min != null || max != null) {
      let rangeStr: string;
      if (min != null && max != null) {
        rangeStr = `${min} - ${max}`;
      } else if (min != null) {
        rangeStr = `${min}+`;
      } else {
        rangeStr = `Up to ${max!}`;
      }
      result.push({
        filterName: 'Average ER',
        value: [rangeStr],
      });
    }
  }

  return result;
};
